/**
 * Security panel that lives inside the Profile page.
 *
 * Surfaces five Phase-2 controls:
 *   1. Change password (with strength meter)
 *   2. Logout from all devices (bumps token_version → kills every JWT)
 *   3. Active sessions list with per-row revoke
 *   4. MFA enable / disable (TOTP via authenticator + backup codes)
 *   5. Read-only display of remaining backup codes
 */
import { useEffect, useState } from "react";
import { Loader2, KeyRound, LogOut, MonitorSmartphone, ShieldCheck, ShieldX, Copy, X } from "lucide-react";
import api, { formatDetail } from "@/lib/api";

const PASSWORD_RULES = [
    { label: "At least 10 characters", test: (s) => (s || "").length >= 10 },
    { label: "Lowercase letter", test: (s) => /[a-z]/.test(s) },
    { label: "Uppercase letter", test: (s) => /[A-Z]/.test(s) },
    { label: "Digit", test: (s) => /\d/.test(s) },
    { label: "Symbol (!@#$…)", test: (s) => /[!@#$%^&*()_+\-=\[\]{};:'",.<>/?\\|`~]/.test(s) },
];

export function SecurityPanel() {
    return (
        <div className="space-y-5">
            <h2 className="text-xl font-semibold tracking-tight">Security</h2>
            <ChangePassword />
            <MfaSection />
            <SessionsSection />
        </div>
    );
}

function ChangePassword() {
    const [open, setOpen] = useState(false);
    const [current, setCurrent] = useState("");
    const [next, setNext] = useState("");
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState("");
    const [err, setErr] = useState("");
    const passed = PASSWORD_RULES.filter((r) => r.test(next)).length;
    const allOk = passed === PASSWORD_RULES.length;

    const submit = async (e) => {
        e.preventDefault(); setErr(""); setMsg(""); setBusy(true);
        try {
            await api.post("/auth/change-password", { current_password: current, new_password: next });
            setMsg("Password updated. Other devices have been signed out.");
            setCurrent(""); setNext("");
        } catch (e) { setErr(formatDetail(e?.response?.data?.detail) || e.message); }
        finally { setBusy(false); }
    };

    return (
        <div className="card p-5">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <KeyRound className="w-4 h-4 text-slate-600" />
                    <div>
                        <div className="font-medium">Password</div>
                        <div className="text-xs text-slate-500">Min 10 chars · mixed case · digit · symbol</div>
                    </div>
                </div>
                <button data-testid="sec-toggle-change-pw" type="button" className="btn-secondary px-3 py-1.5 rounded-md text-xs"
                        onClick={() => { setOpen(!open); setErr(""); setMsg(""); }}>
                    {open ? "Close" : "Change password"}
                </button>
            </div>
            {open && (
                <form onSubmit={submit} className="mt-4 space-y-3 text-sm">
                    <div>
                        <label className="label">Current password</label>
                        <input data-testid="sec-current-pw" required type="password" value={current}
                               onChange={(e) => setCurrent(e.target.value)} className="input" />
                    </div>
                    <div>
                        <label className="label">New password</label>
                        <input data-testid="sec-new-pw" required type="password" value={next}
                               onChange={(e) => setNext(e.target.value)} className="input" autoComplete="new-password" />
                        <div className="mt-2 space-y-1">
                            <div className="flex gap-1">
                                {[0, 1, 2, 3, 4].map((i) => (
                                    <div key={i} className={`h-1 flex-1 rounded ${i < passed ? (passed === 5 ? "bg-emerald-500" : passed >= 4 ? "bg-blue-500" : passed >= 3 ? "bg-amber-500" : "bg-rose-500") : "bg-slate-200"}`} />
                                ))}
                            </div>
                            <ul className="text-[11px] text-slate-500 grid grid-cols-2 gap-x-2 gap-y-0.5">
                                {PASSWORD_RULES.map((r) => (
                                    <li key={r.label} className={r.test(next) ? "text-emerald-600" : ""}>
                                        {r.test(next) ? "✓" : "·"} {r.label}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                    {err && <div data-testid="sec-pw-err" className="text-rose-600 text-xs">{err}</div>}
                    {msg && <div data-testid="sec-pw-msg" className="text-emerald-600 text-xs">{msg}</div>}
                    <div className="flex justify-end">
                        <button data-testid="sec-submit-pw" disabled={busy || !allOk || !current} type="submit"
                                className="btn-primary px-4 py-2 rounded-md text-sm flex items-center gap-2">
                            {busy && <Loader2 className="w-4 h-4 animate-spin" />} Update password
                        </button>
                    </div>
                </form>
            )}
        </div>
    );
}

function MfaSection() {
    const [status, setStatus] = useState(null);
    const [setup, setSetup] = useState(null);          // {secret, qr_data_url, backup_codes, otpauth_uri}
    const [code, setCode] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const [disablePw, setDisablePw] = useState("");
    const [disableCode, setDisableCode] = useState("");
    const [showDisable, setShowDisable] = useState(false);

    const reload = () => api.get("/auth/mfa/status").then((r) => setStatus(r.data));
    useEffect(() => { reload(); }, []);

    const startSetup = async () => {
        setErr(""); setBusy(true);
        try { const r = await api.post("/auth/mfa/setup"); setSetup(r.data); }
        catch (e) { setErr(formatDetail(e?.response?.data?.detail) || e.message); }
        finally { setBusy(false); }
    };

    const finishSetup = async (e) => {
        e.preventDefault(); setErr(""); setBusy(true);
        try {
            await api.post("/auth/mfa/enable", { code: code.replace(/\s/g, "") });
            setSetup(null); setCode("");
            await reload();
        } catch (e) { setErr(formatDetail(e?.response?.data?.detail) || e.message); }
        finally { setBusy(false); }
    };

    const disable = async (e) => {
        e.preventDefault(); setErr(""); setBusy(true);
        try {
            await api.post("/auth/mfa/disable", { code: disableCode || undefined, password: disablePw || undefined });
            setDisableCode(""); setDisablePw(""); setShowDisable(false);
            await reload();
        } catch (e) { setErr(formatDetail(e?.response?.data?.detail) || e.message); }
        finally { setBusy(false); }
    };

    if (!status) return null;

    return (
        <div className="card p-5">
            <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                    {status.enabled
                        ? <ShieldCheck className="w-4 h-4 text-emerald-600" />
                        : <ShieldX className="w-4 h-4 text-slate-400" />}
                    <div>
                        <div className="font-medium">Two-factor authentication (TOTP)</div>
                        <div className="text-xs text-slate-500">
                            {status.enabled
                                ? `Enabled · ${status.backup_codes_remaining} backup code${status.backup_codes_remaining === 1 ? "" : "s"} left`
                                : "Disabled — anyone with your password can sign in."}
                        </div>
                    </div>
                </div>
                {!status.enabled && !setup && (
                    <button data-testid="sec-mfa-enable-btn" type="button" disabled={busy}
                            onClick={startSetup}
                            className="btn-primary px-3 py-1.5 rounded-md text-xs flex items-center gap-2">
                        {busy && <Loader2 className="w-3 h-3 animate-spin" />} Enable MFA
                    </button>
                )}
                {status.enabled && (
                    <span data-testid="sec-mfa-mandatory" className="text-[11px] text-slate-500">Mandatory · cannot be disabled</span>
                )}
            </div>

            {setup && (
                <div className="mt-4 border-t border-slate-200 pt-4 space-y-3 text-sm">
                    <div className="text-xs text-slate-600">
                        1. Scan this QR with Google Authenticator, Authy, 1Password or any TOTP app.
                    </div>
                    <div className="flex items-start gap-4 flex-wrap">
                        <img data-testid="sec-mfa-qr" src={setup.qr_data_url} alt="MFA QR" className="w-40 h-40 border border-slate-200 rounded" />
                        <div className="flex-1 min-w-[200px]">
                            <div className="text-xs text-slate-500">Or enter the key manually:</div>
                            <div className="font-mono text-[11px] break-all bg-slate-50 border border-slate-200 rounded p-2 mt-1">{setup.secret}</div>
                            <div className="mt-3">
                                <div className="text-xs text-slate-500 mb-1">Backup codes (save these — shown once):</div>
                                <div className="grid grid-cols-2 gap-1 font-mono text-[11px]">
                                    {setup.backup_codes.map((c) => (
                                        <div key={c} className="bg-amber-50 border border-amber-200 rounded px-2 py-1">{c}</div>
                                    ))}
                                </div>
                                <button type="button" onClick={() => navigator.clipboard.writeText(setup.backup_codes.join("\n"))}
                                        className="text-[11px] text-blue-600 hover:underline mt-2 flex items-center gap-1">
                                    <Copy className="w-3 h-3" /> Copy all
                                </button>
                            </div>
                        </div>
                    </div>
                    <form onSubmit={finishSetup} className="flex items-end gap-2">
                        <div className="flex-1">
                            <label className="label">2. Enter a 6-digit code from the app</label>
                            <input data-testid="sec-mfa-verify-code" required value={code} onChange={(e) => setCode(e.target.value)}
                                   inputMode="numeric" className="input tracking-widest text-center" placeholder="123456" />
                        </div>
                        <button data-testid="sec-mfa-verify-btn" disabled={busy} type="submit" className="btn-primary px-4 py-2 rounded-md text-sm flex items-center gap-2">
                            {busy && <Loader2 className="w-4 h-4 animate-spin" />} Activate
                        </button>
                    </form>
                </div>
            )}

            {showDisable && status.enabled && (
                <form onSubmit={disable} className="mt-4 border-t border-slate-200 pt-4 space-y-2 text-sm">
                    <p className="text-xs text-slate-600">Provide EITHER a current 6-digit code OR your password to disable.</p>
                    <input data-testid="sec-mfa-disable-code" value={disableCode} onChange={(e) => setDisableCode(e.target.value)}
                           className="input" placeholder="6-digit code or backup code" />
                    <div className="text-center text-[10px] text-slate-400">— or —</div>
                    <input data-testid="sec-mfa-disable-pw" type="password" value={disablePw} onChange={(e) => setDisablePw(e.target.value)}
                           className="input" placeholder="Account password" autoComplete="current-password" />
                    <div className="flex justify-end">
                        <button data-testid="sec-mfa-disable-submit" disabled={busy || (!disableCode && !disablePw)} type="submit"
                                className="btn-primary px-4 py-2 rounded-md text-sm">
                            {busy ? "Disabling…" : "Confirm disable"}
                        </button>
                    </div>
                </form>
            )}

            {err && <div data-testid="sec-mfa-err" className="text-rose-600 text-xs mt-3">{err}</div>}
        </div>
    );
}

function SessionsSection() {
    const [items, setItems] = useState([]);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    const reload = () => api.get("/auth/sessions").then((r) => setItems(r.data.items || []));
    useEffect(() => { reload(); }, []);

    const revoke = async (jti) => {
        setErr(""); setBusy(true);
        try { await api.delete(`/auth/sessions/${jti}`); await reload(); }
        catch (e) { setErr(formatDetail(e?.response?.data?.detail) || e.message); }
        finally { setBusy(false); }
    };

    const logoutAll = async () => {
        if (!window.confirm("Sign out from EVERY device, including this one?")) return;
        setBusy(true);
        try {
            await api.post("/auth/logout-all");
            window.location.href = "/";
        } catch (e) { setErr(formatDetail(e?.response?.data?.detail) || e.message); setBusy(false); }
    };

    return (
        <div className="card p-5">
            <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                    <MonitorSmartphone className="w-4 h-4 text-slate-600" />
                    <div>
                        <div className="font-medium">Active sessions</div>
                        <div className="text-xs text-slate-500">Devices currently signed in to your account.</div>
                    </div>
                </div>
                <button data-testid="sec-logout-all" type="button" disabled={busy} onClick={logoutAll}
                        className="btn-secondary px-3 py-1.5 rounded-md text-xs flex items-center gap-2 text-rose-600 border-rose-200 hover:bg-rose-50">
                    <LogOut className="w-3 h-3" /> Sign out everywhere
                </button>
            </div>
            <div className="mt-3 space-y-1.5">
                {items.map((s) => (
                    <div key={s.jti} data-testid={`sec-session-${s.jti}`} className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded px-3 py-2 text-xs">
                        <div className="min-w-0 flex-1 mr-2">
                            <div className="font-medium text-slate-800 truncate">{s.current ? "This device" : (s.ua?.split(" ").slice(-1)[0] || "Unknown")} <span className="ml-1 text-slate-400">· {s.ip}</span></div>
                            <div className="text-slate-500 truncate">Active {new Date(s.last_seen).toLocaleString()}</div>
                        </div>
                        {!s.current ? (
                            <button data-testid={`sec-revoke-${s.jti}`} type="button" disabled={busy}
                                    onClick={() => revoke(s.jti)}
                                    className="btn-secondary px-2 py-1 rounded text-[11px] text-rose-600 hover:bg-rose-50 flex items-center gap-1">
                                <X className="w-3 h-3" /> Revoke
                            </button>
                        ) : <span className="text-[10px] uppercase tracking-wider text-emerald-600">Current</span>}
                    </div>
                ))}
                {items.length === 0 && <div className="text-xs text-slate-400">No sessions.</div>}
            </div>
            {err && <div className="text-rose-600 text-xs mt-2">{err}</div>}
        </div>
    );
}
